// Pure decision logic of the desktop sidebar hover rule (owner A21/A22, R18, R19).
// Sidebar.tsx feeds it pointer positions and layout rectangles; it decides whether the hover
// timer runs, for which rail section, and what happens when it fires. No DOM, no React:
// covered by node --test in __tests__/sidebarHover.test.ts.
//
// The rule works only on the pointer POSITION against fixed rectangles: the rail (left + 72 px)
// and the open panel (left + 256 px, plus the 12 px the toggle sticks out). The rail icons keep
// the same x/y in the closed rail and in the open panel (rail column + section content to the
// right), so the pointer never lands on a menu link by a hover-open.

export const RAIL_W = 72
export const PANEL_W = 256 + 12
/** Hover intent delay; restarts for each rail icon the pointer moves onto. */
export const HOVER_OPEN_MS = 150
/** Timer target for the rail outside an icon (closed rail only: opens the active section). */
export const RAIL_BG = ''

export type Pt = { x: number; y: number }
export type Geo = { left: number; top: number; bottom: number }
/** One rail icon row, viewport y (the icon spans the whole rail width). */
export type RailIcon = { section: string; top: number; bottom: number }

export const within = (g: Geo, p: Pt | null, w: number): boolean =>
  !!p && p.x >= g.left && p.x <= g.left + w && p.y >= g.top && p.y <= g.bottom

/**
 * Layout rectangle of the nav WITHOUT CSS transforms. offsetLeft/offsetTop/offsetHeight ignore
 * `translate`, so the mobile -> desktop slide (-translate-x-full -> 0) cannot store a wrong rail.
 */
export function navGeo(el: { offsetLeft: number; offsetTop: number; offsetHeight: number }): Geo {
  return { left: el.offsetLeft, top: el.offsetTop, bottom: el.offsetTop + el.offsetHeight }
}

/**
 * Rail icon rows from client rects. The nav and its icons move by the same translate, so the
 * icon offset inside the nav (iconRect - navRect) plus the transform-free nav top is exact.
 */
export function railIcons(g: Geo, navRectTop: number, icons: { section: string; top: number; bottom: number }[]): RailIcon[] {
  return icons.map((i) => ({ section: i.section, top: g.top + (i.top - navRectTop), bottom: g.top + (i.bottom - navRectTop) }))
}

/** Section of the rail icon under the pointer, or null (not on the rail, or between icons). */
export function railIconAt(g: Geo, icons: RailIcon[], p: Pt | null): string | null {
  if (!p || !within(g, p, RAIL_W)) return null
  return icons.find((i) => p.y >= i.top && p.y <= i.bottom)?.section ?? null
}

export type RuleState = {
  /** Desktop overlay open. */
  open: boolean
  /** Hover-open waits until the pointer was outside the rail (after a close / a drag onto it). */
  blocked: boolean
  /** A mouse button is down. */
  pressed: boolean
  /** An HTML5 drag (page element or external file) runs. */
  dragging: boolean
  /** Section open in the panel (accordion). */
  shown: string | null
  /** Target the running hover timer was started for (null = no timer). */
  armed: string | null
}

export type RuleOut = {
  blocked: boolean
  /** Pointer inside the closed rail / the open panel (no idle countdown while inside). */
  inside: boolean
  /** Section the timer must run for: a section, RAIL_BG, or null = no timer. */
  target: string | null
  timer: 'keep' | 'cancel' | 'restart'
}

/** Hover timer target for this position: closed rail -> icon or RAIL_BG; open -> another icon. */
export function hoverTarget(s: Omit<RuleState, 'armed'>, g: Geo, icons: RailIcon[], p: Pt | null): string | null {
  if (s.pressed || s.dragging || !within(g, p, RAIL_W)) return null
  const icon = railIconAt(g, icons, p)
  if (!s.open) return s.blocked ? null : icon ?? RAIL_BG
  // Open: the rail stays in place. Only ANOTHER section's icon switches; the shown one only keeps.
  return icon != null && icon !== s.shown ? icon : null
}

/** Re-evaluates the rule for a new pointer position (pointermove/down/up, drag, window leave). */
export function evaluate(s: RuleState, g: Geo, icons: RailIcon[], p: Pt | null): RuleOut {
  const onRail = within(g, p, RAIL_W)
  const blocked = onRail ? s.blocked : false
  const inside = s.open ? within(g, p, PANEL_W) : onRail
  const target = hoverTarget({ ...s, blocked }, g, icons, p)
  const timer = target == null ? 'cancel' : target === s.armed ? 'keep' : 'restart'
  return { blocked, inside, target, timer }
}

/** A close with the pointer on the rail blocks hover-open until the pointer leaves the rail. */
export const blockedAfterClose = (g: Geo, p: Pt | null): boolean => within(g, p, RAIL_W)

/** A press that moved (a drag) and ends on the rail must not open it right away (R18). */
export const blockedAfterPress = (pressMoved: boolean, g: Geo, p: Pt | null): boolean =>
  pressMoved && within(g, p, RAIL_W)

/** What the element at the pointer says at fire time (document.elementFromPoint). */
export type Hit = {
  /** The topmost element at the pointer is inside the nav (no palette / modal / drawer above it). */
  inNav: boolean
  /** data-rail-section of the rail icon at the pointer, if any. */
  section: string | null
}

export type FireAction = { kind: 'open'; section: string | null } | { kind: 'switch'; section: string } | null

/** Decision when the hover timer fires: open the overlay, switch the section, or nothing. */
export function fireAction(s: Omit<RuleState, 'armed'>, g: Geo, icons: RailIcon[], p: Pt | null, hit: Hit): FireAction {
  const target = hoverTarget(s, g, icons, p)
  if (target == null || !hit.inNav) return null
  // The section under the pointer NOW decides (the hit test is the truth, geometry the fallback).
  const section = hit.section ?? (target === RAIL_BG ? null : target)
  if (!s.open) return { kind: 'open', section }
  // Open: only a real rail icon under the pointer switches. The geometric rail zone reaches into
  // the left edge of the menu links (x 84..88), and a switch there would move a link under the click.
  return hit.section != null && hit.section !== s.shown ? { kind: 'switch', section: hit.section } : null
}
